import { useEffect, useCallback, useState, useMemo, useRef } from "react";
import { useAtom } from "jotai";
import { useEditor, type Editor } from "@tiptap/react";
import {
  tiptapTextInputHandler,
  tiptapAutocompleteStateAtom,
} from "../handlers/tiptap-text-input-handler";
import { activeChatDraftTextAtom } from "../state/chat";
import { useTags } from "./useTags";
import type { TagSearchResult, Tag } from "@pineseed/gui/types";

/**
 * Tiptap Text Input Hook
 * STRICT ARCHITECTURE COMPLIANCE: Hook layer only
 * - Bridges handler and state atoms
 * - No business logic
 * - No UI code
 * - Sets up handler callbacks in useEffect
 */

export function useTiptapTextInput() {
  const [autocompleteState, setAutocompleteState] = useAtom(
    tiptapAutocompleteStateAtom,
  );
  const [draftText, setDraftText] = useAtom(activeChatDraftTextAtom);
  const { searchTags, createNewTag } = useTags();
  const [currentText, setCurrentText] = useState("");
  const [hasText, setHasText] = useState(false);
  const hasTextRef = useRef(false);

  // Track editing state to prevent draft overwrites during active editing
  const isEditingRef = useRef(false);
  const lastUserInputRef = useRef(0);
  const draftLoadedRef = useRef(false);

  // Memoize the callback functions to prevent editor recreation
  const onUpdateCallback = useCallback(({ editor }: { editor: Editor }) => {
    // Mark as actively editing and track user input timing
    isEditingRef.current = true;
    lastUserInputRef.current = Date.now();

    // Update our reactive state when editor content changes
    const newText = editor.getText();
    const textHasContent = newText.trim().length > 0;
    setCurrentText(newText);
    
    // Only update hasText on empty ↔ non-empty transitions
    if (hasTextRef.current !== textHasContent) {
      hasTextRef.current = textHasContent;
      setHasText(textHasContent);
    }

    // Handle draft text saving (preserves mentions as JSON)
    tiptapTextInputHandler.handleContentChange(editor);

    // Reset editing flag after a delay to allow draft loading when user stops typing
    setTimeout(() => {
      if (Date.now() - lastUserInputRef.current >= 1000) {
        isEditingRef.current = false;
      }
    }, 1000);
  }, []);

  const onCreateCallback = useCallback(({ editor }: { editor: Editor }) => {
    console.log("editor created?", !!editor);
  }, []);

  const onDestroyCallback = useCallback(() => {
    // Editor destroyed
    isEditingRef.current = false;
    draftLoadedRef.current = false;
  }, []);

  // Create Tiptap editor with performance optimization
  const editorConfig = useMemo(() => {
    const baseConfig = tiptapTextInputHandler.createEditorConfig();
    return {
      ...baseConfig,
      onUpdate: onUpdateCallback,
      onCreate: onCreateCallback,
      onDestroy: onDestroyCallback,
      // Performance optimization to prevent unnecessary re-renders
      shouldRerenderOnTransaction: false,
      // Improve performance by reducing unnecessary updates
      editorProps: {
        ...baseConfig.editorProps,
        handleKeyDown: () => false, // Let our custom handler manage key events
      },
    };
  }, [onUpdateCallback, onCreateCallback, onDestroyCallback]);

  const editor = useEditor(editorConfig);

  // Set up handler callbacks
  useEffect(() => {
    tiptapTextInputHandler.setCallbacks({
      onStateChange: (state) => {
        // Update autocomplete state atom
        setAutocompleteState(state);
      },
      onTagSearch: (query: string) => {
        return searchTags(query);
      },
      onTagCreate: (name: string) => {
        const tag = createNewTag(name, "general", {
          description: `Created from mention: ${name}`,
        });

        if (tag) {
          // Ensure the tag has proper Date objects
          const normalizedTag: Tag = {
            ...tag,
            createdAt:
              tag.createdAt instanceof Date
                ? tag.createdAt
                : new Date(tag.createdAt),
            updatedAt:
              tag.updatedAt instanceof Date
                ? tag.updatedAt
                : new Date(tag.updatedAt),
          };
          return {
            tag: normalizedTag,
            matchScore: 1.0,
            matchType: "name" as const,
          };
        }

        return null;
      },
      onDraftTextChange: (text: string) => {
        // Only save draft if not currently loading from draft to prevent circular updates
        if (!draftLoadedRef.current) {
          setDraftText(text);
        }
      },
    });
  }, [searchTags, createNewTag, setAutocompleteState, setDraftText]);

  // Set editor instance in handler when it's created
  useEffect(() => {
    if (editor) {
      tiptapTextInputHandler.setEditor(editor);
      // Initialize current text state
      const initialText = editor.getText();
      setCurrentText(initialText);
      setHasText(initialText.trim().length > 0);
    }

    return () => {
      if (editor) {
        tiptapTextInputHandler.setEditor(null);
      }
    };
  }, [editor]);

  // Load draft text when conversation changes - FIXED to prevent overwrites during editing
  useEffect(() => {
    if (editor && draftText !== undefined) {
      // Only load draft if:
      // 1. Not currently editing (prevents overwriting user input)
      // 2. Draft content is different from current content
      // 3. User hasn't typed recently
      const currentContent = editor.getText();
      const timeSinceLastInput = Date.now() - lastUserInputRef.current;

      // Compare content properly - if draft is JSON, compare with current JSON
      let isDifferent = false;
      try {
        const draftJson = JSON.parse(draftText);
        const currentJson = editor.getJSON();
        isDifferent = JSON.stringify(draftJson) !== JSON.stringify(currentJson);
      } catch {
        // Draft is plain text, compare with plain text
        isDifferent = draftText !== currentContent;
      }

      if (!isEditingRef.current && isDifferent && timeSinceLastInput > 2000) {
        // Mark that we're loading from draft to prevent circular updates
        draftLoadedRef.current = true;

        // Cancel any pending saves to prevent conflicts
        tiptapTextInputHandler.cancelDraftSave();

        // Set editor content based on draft text (handles both JSON and plain text)
        const contentToLoad = draftText || "";
        try {
          const jsonContent = JSON.parse(contentToLoad);
          editor.commands.setContent(jsonContent);
        } catch {
          editor.commands.setContent(contentToLoad);
        }

        // Update local state to match
        const newText = editor.getText();
        setCurrentText(newText);
        setHasText(newText.trim().length > 0);

        // Reset the flag after content is loaded
        setTimeout(() => {
          draftLoadedRef.current = false;
        }, 100);
      }
    }
  }, [editor, draftText]);

  // Handler methods (delegated to handler)
  const handleTagSelect = useCallback((result: TagSearchResult): boolean => {
    // Mark as editing when tags are selected
    isEditingRef.current = true;
    lastUserInputRef.current = Date.now();
    return tiptapTextInputHandler.handleTagSelect(result);
  }, []);

  const handleTagSelectByIndex = useCallback((index: number): boolean => {
    // Mark as editing when tags are selected
    isEditingRef.current = true;
    lastUserInputRef.current = Date.now();
    return tiptapTextInputHandler.handleTagSelectByIndex(index);
  }, []);

  const handleCreateTag = useCallback(
    (name: string): TagSearchResult | null => {
      return tiptapTextInputHandler.handleCreateTag(name);
    },
    [],
  );

  const stopAutocomplete = useCallback(() => {
    tiptapTextInputHandler.stopAutocomplete();
  }, []);

  const clearText = useCallback(() => {
    // Cancel any pending draft saves first
    tiptapTextInputHandler.cancelDraftSave();

    // Reset editing state
    isEditingRef.current = false;
    draftLoadedRef.current = false;
    lastUserInputRef.current = 0;

    // Clear state
    setDraftText("");
    setCurrentText("");
    setHasText(false);

    // Clear editor
    if (editor) {
      editor.commands.setContent("");
    }
  }, [editor, setDraftText]);

  const getText = useCallback((): string => {
    // Always get the latest text from the editor if available
    if (editor) {
      const editorText = editor.getText();
      return editorText;
    }
    return currentText;
  }, [editor, currentText]);

  const getContentWithTags = useCallback((): string => {
    // Get content with Tag boundaries preserved for message sending
    return tiptapTextInputHandler.getContentWithTags();
  }, []);

  const setText = useCallback(
    (text: string) => {
      // Mark as editing when text is set programmatically
      isEditingRef.current = true;
      lastUserInputRef.current = Date.now();

      tiptapTextInputHandler.setText(text);
      setCurrentText(text);
      setHasText(text.trim().length > 0);
      // Also directly set the editor content if available
      if (editor) {
        try {
          // Try to parse as JSON first (rich content with mentions)
          const jsonContent = JSON.parse(text);
          editor.commands.setContent(jsonContent);
          // Update current text to reflect the actual text content
          const actualText = editor.getText();
          setCurrentText(actualText);
          setHasText(actualText.trim().length > 0);
        } catch {
          // If not JSON, treat as plain text
          editor.commands.setContent(text);
          setCurrentText(text);
          setHasText(text.trim().length > 0);
        }
      }
    },
    [editor],
  );

  const focus = useCallback(() => {
    tiptapTextInputHandler.focus();
  }, []);

  return {
    // Editor instance
    editor,

    // State
    autocompleteState,
    currentText,
    hasText,

    // Methods
    handleTagSelect,
    handleTagSelectByIndex,
    handleCreateTag,
    stopAutocomplete,
    clearText,
    getText,
    getContentWithTags,
    setText,
    focus,
  };
}
